import { defineStore } from "pinia";
import axios from 'axios';
import { AgencyPopulation, AgencyStaffing, OriInfo, SelectItem, UploadType, UserOri } from "@/types";
import { BFieldSelection } from "@/components/BFilterEditor/BFilterEditor.types";
import { ref, computed, Ref, watch } from "vue";
import { handleError } from "./storeHelper";


export interface OriDetails {
    ori_7: string
    ori_9_fbi: string
    ori_7_fbi: string
    agency_type_code: number
    county_code: string
    latitude: number
    longitude: number
    rms_vendor: any
    mail_street: string
    mail_town: string
    mail_state: string
    mail_zip: string
    loc_street: string
    loc_town: string
    loc_state: string
    loc_zip: string
    last_update: string
    start_date: string
    stop_date: string
    admin_title: string
    admin_name: string
    admin_email: string
    admin_phone: string
    admin_fax: string
    ucr_title: string
    ucr_name: string
    ucr_email: string
    ucr_phone: string
    ucr_fax: string
    comm_title: string
    comm_name: string
    comm_email: string
    comm_phone: string
    comm_fax: string
    load_id: number
    fn_symbol_set: any
    item_note: any
    to_fbi: boolean
    ori: string
    ori_name: string
    is_reporting: number
}

interface Ori {
    ori: string;
    agency: string;
    oriAgency: string;
}

interface DatasetDetails {
    dataset_id: number;
    name: string;
    label: string;
    start_date: Date
    end_date: Date
    agency_start_date: Date
    agency_end_date: Date
    vendor_code: string;
}

export const useOriStore = defineStore('oriStore', () => {

    var ori = ref<OriDetails>();
    var oris = ref([] as Ori[]);
    var dataset = ref(undefined as (undefined | UploadType));
    var selectedOri = ref("" as string);
    var datasetDetailArray = ref([] as DatasetDetails[]);

    watch(dataset, fetchOris);

    var getOriName = computed(() => {
        return oris.value.find(f => f.ori == selectedOri.value)?.agency;
    });

    var getOrisForSelectList = computed(() => {
        return oris.value.map(it => ({
            label: it.oriAgency,
            value: it.ori
        } as SelectItem));
    });

    var getOrisAsBFieldSelectionArray = computed(() => {        
        return oris.value.map(it => ({
            text: it.oriAgency,
            value: it.ori
        } as BFieldSelection));
    });

    var getDetailsForCurrentDataset = computed(() => {
        let current = {} as DatasetDetails;
        var dsName = '';

        switch (dataset.value) {
            case UploadType.nibrs:
                dsName = 'NIBRS';
                break;
            case UploadType.summary:
                dsName = 'SUMMARY';
                break;
            case UploadType.useOfForce:
                dsName = 'UOF';
                break;
            case UploadType.policeEmployee:
                dsName = 'PE';
                break;
            case UploadType.contacts:
                dsName = 'CONTACTS';
                break;
            case UploadType.communityPolicing:
                dsName = 'VACP';
                break;           
            case UploadType.population:
                dsName = 'population';
                break;
        }

        datasetDetailArray.value.forEach((ds: DatasetDetails) => {

            if (ds.name == dsName) {
                current = ds;
            }
        });
        return current;
    });

    const getOri = async (oriId: string) => {
        try {
            const data = await axios.get<OriDetails>(`api/v1/ori/${oriId}`);
            ori.value = data.data;
        }
        catch (error) {
            console.error(error);
            throw error;
        }
    }

    async function fetchAll() {
        await fetchOris();
    }

    async function fetchOris() {
        try {
            var url = 'api/v1/ori/filter-info/';

            if( dataset.value != undefined){
                url += dataset.value;
            }

            const data = await axios.get(url);
            oris.value = data.data;
        }
        catch (error) {
            console.error(error);
        }
    }

    async function fetchDatasetDetailsForOri() {
        try {

            if (selectedOri.value != '') {

                const data = await axios.get<DatasetDetails[]>(`api/v1/ori/${selectedOri.value}/dataset-details`);

                datasetDetailArray.value = data.data;
            }
        }
        catch (error) {
            console.error(error);
        }
    }

    function reset() {
        oris.value = [] as Ori[];
        dataset.value = undefined;
        selectedOri.value = "" as string;
        datasetDetailArray.value = [] as DatasetDetails[];
    }

    var getSelectedOri = computed(() => { return selectedOri ?? ""; });

    function setSelectedOri(ori: string) {
        selectedOri.value = ori;
    }

    const loading = ref(false);

    const population = ref<AgencyPopulation>();
    const staffing = ref<AgencyStaffing>();

    const getPopulation = async (ori: string) => {

        try {
            loading.value = true;
            const response = await axios.get<AgencyPopulation>(`api/v1/ori/${ori}/population`);
            population.value = response.data;
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    const getStaffing = async (ori: string) => {

        try {
            loading.value = true;
            const response = await axios.get<AgencyStaffing>(`api/v1/ori/${ori}/staffing`);
            staffing.value = response.data;
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }

    }

    const getOriListForDataset = async (dataset: string) => {
        let oriList = [] as OriInfo[];

        try {
            loading.value = true;
            const response = await axios.get<OriInfo[]>(`api/v1/ori/dataset/${dataset}`);
            oriList = response.data;
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
        return oriList;
    }

    return {
        oris, selectedOri, dataset, getOriName, getOrisForSelectList, getOrisAsBFieldSelectionArray,
        getDetailsForCurrentDataset, fetchOris, fetchAll, fetchDatasetDetailsForOri, reset, getSelectedOri, setSelectedOri, getOri, ori,
        getStaffing, staffing,
        getPopulation, population, 
        getOriListForDataset
    }

},
    {
        persist: true
    })

